import api, { succeedOn404 } from './api';
import type { AxiosResponse } from 'axios';
import type { KeyDate, KeyDateCreate, KeyDateUpdate } from 'bc-artifacts';
import type { OpMetadata } from 'bc-model';
import yup from '../utils/yup';

const dateValidationSchema = yup.object().shape({
  at: yup.number().nullable(),
  atRelDate: yup.mixed().when('atRelOffset', {
    is: (value) =>
      !!value && ((!!value.days && value.days !== '0') || (!!value.hours && value.hours !== '0')),
    then: () => yup.mixed().required('required'),
  }),
  atRelOffset: yup.object().nullable().shape({
    hours: yup.number(),
    days: yup.number(),
  }),
  duration: yup.object().nullable().shape({
    hours: yup.number(),
    days: yup.number(),
  }),
  icon: yup.string(),
  title: yup.string().required('required').min(3, 'too_short').max(40, 'too_long'),
  isAnchor: yup.boolean().nullable(),
});

function trimDatePayload(
  // we're calling this from createDate, which means it needs to accept new and existing dates
  keyDate: KeyDate | KeyDateCreate,
  opMetadata?: OpMetadata
): KeyDateCreate & { opMetadata?: OpMetadata } {
  if ('isAto' in keyDate) {
    return {
      at: keyDate.at,
      briefId: keyDate.briefId,
      icon: keyDate.icon,
      title: keyDate.title,
      atRelDate: keyDate.atRelDate,
      atRelOffset: keyDate.atRelOffset,
      isAnchor: keyDate.isAnchor,
      opMetadata,
      duration: keyDate.duration,
      isAto: keyDate.isAto,
    };
  }
  return {
    at: keyDate.at,
    briefId: keyDate.briefId,
    icon: keyDate.icon,
    title: keyDate.title,
    atRelDate: keyDate.atRelDate,
    atRelOffset: keyDate.atRelOffset,
    isAnchor: keyDate.isAnchor,
    opMetadata,
    duration: keyDate.duration,
  };
}

function trimUpdateDatePayload(date: KeyDate, opMetadata?: OpMetadata): KeyDateUpdate {
  const { id } = date;
  return {
    ...trimDatePayload(date, opMetadata),
    id,
  };
}

async function createDate(
  date: KeyDateCreate,
  opMetadata?: OpMetadata
): Promise<AxiosResponse<KeyDate>> {
  return api.post(`/api/brief/${date.briefId}/dates`, trimDatePayload(date, opMetadata));
}

async function deleteDate(
  briefId: number,
  id: number,
  opMetadata?: OpMetadata
): Promise<AxiosResponse<KeyDate>> {
  return api.delete(`/api/brief/${briefId}/date/${id}`, { data: { opMetadata } });
}

async function updateDate(
  date: KeyDate,
  opMetadata?: OpMetadata
): Promise<AxiosResponse<KeyDate | null>> {
  const { briefId, id } = date;
  return api.put(
    `/api/brief/${briefId}/date/${id}`,
    trimUpdateDatePayload(date, opMetadata),
    succeedOn404
  );
}

async function retrieveDates(briefId: number): Promise<AxiosResponse<{ [id: number]: KeyDate }>> {
  return api.get(`/api/brief/${briefId}/dates`, succeedOn404);
}

async function retrieveDate(briefId: number, dateId: number): Promise<AxiosResponse<KeyDate>> {
  return api.get(`/api/brief/date/${dateId}`, succeedOn404);
}

export {
  createDate,
  dateValidationSchema,
  deleteDate,
  retrieveDate,
  retrieveDates,
  trimDatePayload,
  updateDate,
};
